// Turning a member's highlights into colour on the page, and a selection back
// into highlights.
//
// A highlight is stored as a span of characters in a verse's *plain text*.
// That is the only anchor here that holds: the ESV's markup carries footnote
// markers, poetry lines and verse numbers that we add to and strip from, while
// the wording of Nehemiah 1:3 is fixed. So everything below is built on one
// walk of a verse that produces its plain text and, alongside it, the position
// in the DOM of every character in that text. `verseText` is the same walk with
// the positions thrown away, which is what keeps the two from drifting.
import type { HighlightColor } from "@bibleunleashed/shared";

/** Markers the reader sees but the text does not contain. */
export const SKIP ="b.verse-num, b.chapter-num, sup.footnote, sup.crossref";

/** Where a character sits in the DOM: a text node and an index into it. */
interface Point {
  node: Text;
  offset: number;
}

export interface VerseTextMap {
  /** The verse's plain text: markers gone, whitespace collapsed, trimmed. */
  text: string;
  /**
   * `points[i]` is the position immediately before `text[i]`, and
   * `points[text.length]` the position after the last character — so a span
   * `[start, end)` becomes a Range from `points[start]` to `points[end]`.
   */
  points: Point[];
}

// \u00a0 among them: the ESV separates a verse number from its text with one.
const isSpace = (ch: string) => ch === " " || ch === "\n" || ch === "\t" || ch === "\r" || ch === "\u00a0";

/**
 * Walk one verse across every span that carries it. A verse continuing into a
 * new paragraph has more than one `.v` span; they are joined with a space, the
 * way the printed verse reads.
 */
export function verseTextMap(container: Element, ref: string): VerseTextMap {
  // A ref is only ever "Neh.1.3" — nothing an attribute selector needs escaped.
  const spans = container.querySelectorAll<HTMLElement>(`.v[data-ref="${ref}"]`);
  let text = "";
  const points: Point[] = [];
  let last: Point | undefined;
  // A space is only emitted once a character follows it, which trims the end
  // and collapses runs without a second pass over the string.
  let pendingSpace = false;

  for (const span of spans) {
    if (text) pendingSpace = true;
    const walker = document.createTreeWalker(span, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT, {
      acceptNode: (node) =>
        node.nodeType === Node.ELEMENT_NODE
          ? (node as Element).matches(SKIP)
            ? NodeFilter.FILTER_REJECT
            : NodeFilter.FILTER_SKIP
          : NodeFilter.FILTER_ACCEPT,
    });
    let node: Node | null;
    while ((node = walker.nextNode())) {
      const data = (node as Text).data;
      for (let i = 0; i < data.length; i++) {
        if (isSpace(data[i]!)) {
          pendingSpace = text.length > 0;
          continue;
        }
        if (pendingSpace) {
          // The collapsed space belongs *after* the character before it, not
          // before the one after it. Give it the same position as the upcoming
          // character and the two become indistinguishable, which reads a
          // selection back one character too wide at each end.
          text += " ";
          points.push(last!);
          pendingSpace = false;
        }
        text += data[i];
        points.push({ node: node as Text, offset: i });
        last = { node: node as Text, offset: i + 1 };
      }
    }
  }
  if (last) points.push(last);
  return { text, points };
}

/** Plain text of one verse across all its spans, without markers. */
export function verseText(container: Element, ref: string): string {
  return verseTextMap(container, ref).text;
}

/** The DOM range covering `[start, end)` of a verse's text, if it is on the page. */
function spanRange(map: VerseTextMap, start: number, end: number): Range | undefined {
  const from = map.points[start];
  const to = map.points[end];
  if (!from || !to) return undefined;
  const range = document.createRange();
  range.setStart(from.node, from.offset);
  range.setEnd(to.node, to.offset);
  return range;
}

export interface StoredHighlight {
  ref: string;
  startOffset: number;
  endOffset: number;
  color: HighlightColor;
}

const HIGHLIGHT_NAMES: Record<HighlightColor, string> = {
  yellow: "bu-hl-yellow",
  green: "bu-hl-green",
  blue: "bu-hl-blue",
  pink: "bu-hl-pink",
};

/**
 * The CSS Custom Highlight API, when the browser has it: it paints a range
 * without touching the DOM, which is the whole reason to prefer it. Where it is
 * missing we wrap <mark> elements instead, and that is why every range below is
 * cut into per-text-node slices — a slice never crosses an element boundary, so
 * wrapping one can never produce mis-nested HTML.
 */
interface HighlightRegistry {
  set: (name: string, highlight: object) => void;
  delete: (name: string) => void;
  Highlight: new (...ranges: Range[]) => object;
}

function highlightRegistry(): HighlightRegistry | undefined {
  const g = globalThis as { CSS?: { highlights?: unknown }; Highlight?: unknown };
  const store = g.CSS?.highlights as { set: HighlightRegistry["set"]; delete: HighlightRegistry["delete"] } | undefined;
  if (!store || typeof g.Highlight !== "function") return undefined;
  return {
    set: store.set.bind(store),
    delete: store.delete.bind(store),
    Highlight: g.Highlight as HighlightRegistry["Highlight"],
  };
}

/** Part of one text node: a piece of a highlight that stops at element edges. */
interface Slice {
  node: Text;
  start: number;
  end: number;
}

/**
 * The pieces of a range, one per text node it touches — skipping the markers,
 * so a highlight that runs across a footnote colours the words on either side
 * of it and not the little "1" between them.
 */
function slices(range: Range): Slice[] {
  const out: Slice[] = [];
  const root = range.commonAncestorContainer;
  if (root.nodeType === Node.TEXT_NODE) {
    out.push({ node: root as Text, start: range.startOffset, end: range.endOffset });
    return out;
  }
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let node: Node | null;
  while ((node = walker.nextNode())) {
    const text = node as Text;
    if (!range.intersectsNode(text) || text.parentElement?.closest(SKIP)) continue;
    const start = text === range.startContainer ? range.startOffset : 0;
    const end = text === range.endContainer ? range.endOffset : text.data.length;
    if (end > start) out.push({ node: text, start, end });
  }
  return out;
}

/**
 * Paint a chapter's highlights over the verses currently on the page.
 * Idempotent: it clears what it drew last time first, so it can simply be run
 * again whenever the highlights or the passage change.
 */
export function paintHighlights(container: Element, highlights: readonly StoredHighlight[]): void {
  clearHighlights(container);
  if (!highlights.length) return;

  // One map per verse, however many highlights land on it.
  const maps = new Map<string, VerseTextMap>();
  const mapFor = (ref: string) => {
    let map = maps.get(ref);
    if (!map) {
      map = verseTextMap(container, ref);
      maps.set(ref, map);
    }
    return map;
  };

  // Every slice is worked out against the untouched DOM before anything is
  // drawn, because the <mark> fallback splits text nodes as it goes and would
  // otherwise invalidate the positions of the highlights still to come.
  const pending: { slice: Slice; color: HighlightColor }[] = [];
  for (const h of highlights) {
    const range = spanRange(mapFor(h.ref), h.startOffset, h.endOffset);
    if (!range) continue; // The verse isn't in this block; another block has it.
    for (const slice of slices(range)) pending.push({ slice, color: h.color });
  }

  const registry = highlightRegistry();
  if (!registry) {
    // Back to front: splitting at a later offset leaves everything before it
    // in the original node, at the offsets we already measured.
    for (const { slice, color } of [...pending].sort((a, b) => b.slice.start - a.slice.start)) wrapSlice(slice, color);
    return;
  }

  const byColor = new Map<HighlightColor, Range[]>();
  for (const { slice, color } of pending) {
    const r = document.createRange();
    r.setStart(slice.node, slice.start);
    r.setEnd(slice.node, slice.end);
    const ranges = byColor.get(color) ?? [];
    ranges.push(r);
    byColor.set(color, ranges);
  }
  for (const [color, ranges] of byColor) registry.set(HIGHLIGHT_NAMES[color], new registry.Highlight(...ranges));
}

const SHORT: Record<HighlightColor, string> = { yellow: "", green: "g", blue: "b", pink: "p" };

function wrapSlice(slice: Slice, color: HighlightColor): void {
  const { node, start, end } = slice;
  const tail = node.splitText(start);
  if (end - start < tail.data.length) tail.splitText(end - start);
  const mark = document.createElement("mark");
  mark.dataset.hl = "1";
  if (SHORT[color]) mark.className = SHORT[color]!;
  tail.parentNode?.insertBefore(mark, tail);
  mark.appendChild(tail);
}

/** Undo whichever way the last paint drew. */
export function clearHighlights(container: Element): void {
  const registry = highlightRegistry();
  for (const name of Object.values(HIGHLIGHT_NAMES)) registry?.delete(name);
  for (const mark of Array.from(container.querySelectorAll("mark[data-hl]"))) {
    const parent = mark.parentNode;
    if (!parent) continue;
    while (mark.firstChild) parent.insertBefore(mark.firstChild, mark);
    parent.removeChild(mark);
    parent.normalize();
  }
}

/**
 * A selection, as highlights. One row per verse the selection touches, because
 * offsets are only meaningful inside a single verse; a sweep across three
 * verses is three rows, and each survives independently if the reader later
 * clears one of them.
 */
export function selectionToSpans(container: Element, range: Range): { ref: string; startOffset: number; endOffset: number }[] {
  if (range.collapsed) return [];
  const refs: string[] = [];
  for (const span of container.querySelectorAll<HTMLElement>(".v[data-ref]")) {
    const ref = span.dataset.ref;
    if (!ref || refs.includes(ref)) continue;
    if (range.intersectsNode(span)) refs.push(ref);
  }

  const out: { ref: string; startOffset: number; endOffset: number }[] = [];
  for (const ref of refs) {
    const map = verseTextMap(container, ref);
    if (!map.text) continue;
    // The first character at or after the selection start, and the last at or
    // before its end. Points are in document order, so this is a scan.
    let start = -1;
    let end = -1;
    for (let i = 0; i < map.points.length; i++) {
      const p = map.points[i]!;
      const where = comparePoint(range, p);
      if (where === undefined) continue;
      if (where >= 0 && start === -1) start = i;
      if (where <= 0) end = i;
    }
    if (start === -1 || end <= start) continue;
    out.push({ ref, startOffset: start, endOffset: Math.min(end, map.text.length) });
  }
  return out;
}

/** -1 before the range, 0 inside it, 1 after; undefined if it cannot be compared. */
function comparePoint(range: Range, point: Point): number | undefined {
  try {
    return range.comparePoint(point.node, point.offset);
  } catch {
    return undefined;
  }
}
